import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, User, ShieldCheck, Loader2, Eye, EyeOff, TerminalSquare } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(username.trim(), password);
    setLoading(false);
    if (res.ok) {
      navigate("/", { replace: true });
    } else {
      setError(res.error || "Giriş başarısız");
    }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(59,130,246,0.08),_transparent_55%)]" />
      <div
        className="w-full max-w-md relative z-10 fade-up"
        data-testid="login-form"
      >
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 border"
            style={{ background: "var(--bg-elevated)", borderColor: "var(--border-subtle)", boxShadow: "0 0 30px rgba(59,130,246,0.25)" }}>
            <TerminalSquare className="w-8 h-8 text-blue-500" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">LUNA <span className="text-blue-500">AI</span></h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Profesyonel Grafik Analiz Terminali
          </p>
          <div className="flex items-center gap-1.5 mt-3 text-xs px-3 py-1 rounded-full border"
            style={{ color: "var(--text-muted)", borderColor: "var(--border-subtle)" }}>
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
            Özel Erişim · Tek Kullanıcı
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border p-7 backdrop-blur-xl"
          style={{ background: "rgba(15,22,35,0.7)", borderColor: "var(--border-subtle)" }}
        >
          <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>
            Kullanıcı Adı
          </label>
          <div className="relative mb-5">
            <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
            <input
              data-testid="login-username-input"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-xl pl-10 pr-4 py-3 text-sm outline-none border transition-colors focus:border-blue-500"
              style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }}
              placeholder="admin"
              required
            />
          </div>

          <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>
            Şifre
          </label>
          <div className="relative mb-6">
            <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
            <input
              data-testid="login-password-input"
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl pl-10 pr-11 py-3 text-sm outline-none border transition-colors focus:border-blue-500"
              style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }}
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: "var(--text-muted)" }}
              tabIndex={-1}
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <div
              data-testid="login-error-message"
              className="mb-5 text-sm px-4 py-3 rounded-xl border"
              style={{ background: "rgba(239,68,68,0.1)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-xl py-3.5 font-semibold text-sm text-white transition-all hover:brightness-110 disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: "#3B82F6", boxShadow: "0 0 24px rgba(59,130,246,0.35)" }}
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Doğrulanıyor...</> : "Terminale Giriş Yap"}
          </button>
        </form>

        <p className="text-center text-sm mt-6" style={{ color: "var(--text-muted)" }}>
          Hesabın yok mu?{" "}
          <Link to="/register" data-testid="go-to-register-link" className="text-blue-400 font-semibold hover:underline">
            Kayıt Ol
          </Link>
        </p>
      </div>
    </div>
  );
}
